import { ApolloClient, InMemoryCache } from '@apollo/client';
import { HttpLink } from '@apollo/client/link/http';
import { SetContextLink } from '@apollo/client/link/context';

import { isTokenExpired, refreshAuthToken, clearAuthStorage } from '../utils/auth';
import { GetCookie } from '../utils/cookie';
import { useConfigStore } from '../stores/ConfigStore';
import env from '../env';

export const createApolloClient = (uri: string = env.APP_GRAPHQL_ENDPOINT || '') => {
  const httpLink = new HttpLink({
    uri,
  });

  const authLink = new SetContextLink(async (_: any, requestContext: any) => {
    const { authentication_type } = useConfigStore.getState().featureFlags?.common || {};
    const isKatonCognito = authentication_type === 'katon_cognito';

    let token = GetCookie('AccessToken');
    const refreshToken = GetCookie('RefreshToken');

    // Automatically refresh token if expired
    if (isKatonCognito && token && isTokenExpired(token) && refreshToken) {
      console.log('[Apollo] Token expired, attempting refresh...');
      
      // 1. Capture previous role
      let prevRole = '';
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/')));
        prevRole = JSON.parse(payload.custom_payload || '{}')?.role;
      } catch (error) {
        console.warn('[Apollo] Failed to parse previous role from JWT:', error);
      }

      const newToken = await refreshAuthToken(refreshToken);

      if (newToken) {
        console.log('[Apollo] Token refreshed successfully');
        token = newToken;

        // 2. Check if role changed
        try {
          const payload = JSON.parse(atob(newToken.split('.')[1].replaceAll('-', '+').replaceAll('_', '/')));
          const currentRole = JSON.parse(payload.custom_payload || '{}')?.role;
          if (currentRole && prevRole && currentRole !== prevRole) {
            console.log(`[Apollo] Role changed from ${prevRole} to ${currentRole}. Reloading...`);
            window.location.reload();
          }
        } catch (error) {
          console.warn('[Apollo] Failed to parse new role after refresh:', error);
        }
      } else {
        // 3. If refresh fails, clear and redirect
        console.error('[Apollo] Refresh failed. Logging out...');
        clearAuthStorage();

        window.location.href = '/';
      }
    }

    // return the headers to the context so httpLink can read them
    return {
      headers: {
        ...(requestContext.headers || {}),
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

let apolloClient: ApolloClient | null = null;
export const getApolloClient = () => {
  apolloClient ??= createApolloClient();
  return apolloClient;
};

let authApolloClient: ApolloClient | null = null;
export const getAuthApolloClient = () => {
  authApolloClient ??= createApolloClient(env.APP_API_GRAPHQL_ENDPOINT);
  return authApolloClient;
};
